import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { PMDialog } from "../../utils/PMDialog";
import {
  createOfferRevisionAsync,
  getOfferAsync,
  getOfferRevisionsAsync,
} from "../../../slices/offer";
import {
  isOfferFeatureEnabled,
  resolveOfferActionErrorMessage,
  shouldShowCreateRevisionMenuItem,
} from "./utils";

export { shouldShowCreateRevisionMenuItem };

const EDITABLE_DRAFT_STATUSES = new Set(["draft", "pending_review"]);
const LOCKED_STATUSES = new Set([
  "approved",
  "rejected",
  "sent",
  "issued",
  "accepted",
  "superseded",
]);

function resolveLatestLocked(revisions = []) {
  return [...revisions]
    .sort((a, b) => (b.revision_number || 0) - (a.revision_number || 0))
    .find(
      (r) =>
        r?.locked ||
        (r?.status && LOCKED_STATUSES.has(String(r.status)))
    ) || null;
}

function hasEditableDraft(offer, revisions = []) {
  const draftId = offer?.current_draft_revision_id;
  if (draftId) {
    const draft =
      revisions.find((r) => r.id === draftId) || offer?.current_draft_revision;
    if (!draft) return true;
    return EDITABLE_DRAFT_STATUSES.has(String(draft.status || "draft"));
  }
  return revisions.some((r) =>
    EDITABLE_DRAFT_STATUSES.has(String(r?.status || ""))
  );
}

/**
 * Explains revision creation + offers alternatives (copy / blank) in one dialog.
 * Confirm reuses createOfferRevisionAsync (same path as Approval / Revisjoner).
 */
export default function OfferCreateRevisionDialog({
  open,
  onClose,
  offer,
  featureFlags,
  onRefresh,
  onCopyOffer,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const flags = featureFlags || offer?.offer_feature_flags || {};
  const revisionsEnabled = isOfferFeatureEnabled(flags, "offer_revisions");

  const storeRevisions = useSelector((state) => state.offers?.revisions || []);
  const revisionsOfferId = useSelector(
    (state) => state.offers?.revisionsOfferId
  );
  const revisionsLoading = useSelector(
    (state) => state.offers?.revisionsLoading
  );

  const revisions =
    revisionsOfferId === offer?.id
      ? storeRevisions
      : Array.isArray(offer?.revisions)
        ? offer.revisions
        : [];

  const latestLocked = useMemo(
    () => resolveLatestLocked(revisions),
    [revisions]
  );
  const alreadyHasDraft = useMemo(
    () => hasEditableDraft(offer, revisions),
    [offer, revisions]
  );

  const [option, setOption] = useState("revision");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !offer?.id || !revisionsEnabled) return;
    dispatch(getOfferRevisionsAsync(offer.id));
  }, [open, offer?.id, revisionsEnabled, dispatch]);

  useEffect(() => {
    if (!open) return;
    setOption(alreadyHasDraft ? "copy" : "revision");
  }, [open, alreadyHasDraft]);

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleConfirm = async () => {
    if (!offer?.id || submitting) return;

    if (option === "copy") {
      onClose?.();
      onCopyOffer?.();
      return;
    }

    if (option === "blank") {
      onClose?.();
      navigate("/dashboard/offers/new");
      return;
    }

    if (!revisionsEnabled) {
      toast.error(t("offers.approval.unavailable"));
      return;
    }

    if (alreadyHasDraft) {
      toast.error(t("offers.create_revision.already_draft"));
      return;
    }

    if (!latestLocked?.id) {
      toast.error(t("offers.create_revision.missing_locked"));
      return;
    }

    setSubmitting(true);
    try {
      const result = await dispatch(
        createOfferRevisionAsync({
          offerId: offer.id,
          based_on_revision_id: latestLocked.id,
          replace_draft: true,
          change_note: t("offers.approval.reopen_note", {
            number: latestLocked.revision_number,
          }),
        })
      );

      if (createOfferRevisionAsync.rejected.match(result)) {
        toast.error(
          resolveOfferActionErrorMessage(
            t,
            result.payload,
            "offers.approval.reopen_failed"
          )
        );
        return;
      }

      toast.success(
        t("offers.approval.reopen_success", {
          number: latestLocked.revision_number,
        })
      );
      await dispatch(getOfferAsync(offer.id));
      await onRefresh?.(result.payload);
      onClose?.();
    } catch (err) {
      toast.error(
        resolveOfferActionErrorMessage(t, err, "offers.approval.reopen_failed")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const revisionDisabled =
    alreadyHasDraft || (!latestLocked?.id && !revisionsLoading);
  const confirmDisabled =
    submitting ||
    (option === "revision" &&
      (revisionDisabled || revisionsLoading || !latestLocked?.id));

  const radioClass = (value, disabled) =>
    `flex w-full cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 text-left transition-colors ${
      disabled
        ? "cursor-not-allowed border-slate-100 bg-slate-50 opacity-60"
        : option === value
          ? "border-[#BFDCF7] bg-[#EAF3FD]"
          : "border-slate-200 bg-white hover:bg-slate-50"
    }`;

  return (
    <PMDialog
      open={open}
      onClose={handleClose}
      title="offers.create_revision.title"
      size="lg"
      closeOnOutsideClick={!submitting}
    >
      <div className="space-y-4">
        <div className="space-y-2 text-sm text-slate-600">
          <p>{t("offers.create_revision.intro")}</p>
          <ul className="list-disc space-y-1 pl-5 text-slate-600">
            <li>{t("offers.create_revision.bullet_based_on")}</li>
            <li>{t("offers.create_revision.bullet_locked")}</li>
            <li>{t("offers.create_revision.bullet_draft")}</li>
            <li>{t("offers.create_revision.bullet_kalkyle")}</li>
          </ul>
        </div>

        {alreadyHasDraft && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {t("offers.create_revision.already_draft_hint")}
          </div>
        )}

        {!alreadyHasDraft && latestLocked?.revision_number != null && (
          <p className="text-sm text-slate-700">
            {t("offers.create_revision.based_on_label", {
              number: latestLocked.revision_number,
            })}
          </p>
        )}

        <fieldset className="space-y-2">
          <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t("offers.create_revision.choose_label")}
          </legend>

          <label className={radioClass("revision", revisionDisabled)}>
            <input
              type="radio"
              name="offer-create-revision-option"
              value="revision"
              checked={option === "revision"}
              disabled={revisionDisabled || submitting}
              onChange={() => setOption("revision")}
              className="mt-1"
            />
            <span>
              <span className="block text-sm font-semibold text-slate-900">
                {t("offers.create_revision.option_revision_title")}
              </span>
              <span className="mt-0.5 block text-xs text-slate-500">
                {t("offers.create_revision.option_revision_hint")}
              </span>
            </span>
          </label>

          <label className={radioClass("copy", false)}>
            <input
              type="radio"
              name="offer-create-revision-option"
              value="copy"
              checked={option === "copy"}
              disabled={submitting}
              onChange={() => setOption("copy")}
              className="mt-1"
            />
            <span>
              <span className="block text-sm font-semibold text-slate-900">
                {t("offers.create_revision.option_copy_title")}
              </span>
              <span className="mt-0.5 block text-xs text-slate-500">
                {t("offers.create_revision.option_copy_hint")}
              </span>
            </span>
          </label>

          <label className={radioClass("blank", false)}>
            <input
              type="radio"
              name="offer-create-revision-option"
              value="blank"
              checked={option === "blank"}
              disabled={submitting}
              onChange={() => setOption("blank")}
              className="mt-1"
            />
            <span>
              <span className="block text-sm font-semibold text-slate-900">
                {t("offers.create_revision.option_blank_title")}
              </span>
              <span className="mt-0.5 block text-xs text-slate-500">
                {t("offers.create_revision.option_blank_hint")}
              </span>
            </span>
          </label>
        </fieldset>

        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="inline-flex justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={confirmDisabled}
            className="inline-flex justify-center rounded-lg border border-transparent bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? t("Loading...")
              : option === "revision"
                ? t("offers.create_revision.confirm")
                : option === "copy"
                  ? t("offers.create_revision.confirm_copy")
                  : t("offers.create_revision.confirm_blank")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}
